/**
 * InterviewSession — main session screen.
 *
 * Composition:
 *   - ModeToggle (only when mode not yet selected for this session)
 *   - TranscriptStream (always)
 *   - MicButton (voice mode, non-readonly)
 *   - TextInputBar (text mode, non-readonly)
 *   - Pause banner (when sessionStore.mode === 'paused')
 *   - Error banner (when sessionStore.mode === 'error')
 *
 * iOS gesture chain (Pitfall C): MicButton.onStart calls resumeAudioContext
 * which is passed through from useSession.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../hooks/useSession';
import type { Turn } from '../../store/sessionStore';
import { TranscriptStream } from './TranscriptStream';
import { MicButton } from './MicButton';
import {
  TextInputBar,
  uploadChatPhoto,
  newLocalPhotoId,
  CHAT_IMAGE_TYPES,
  MAX_CHAT_PHOTOS,
  type PendingPhoto,
} from './TextInputBar';
import { fetchWithAuth } from '../../lib/api';

const REST_API_URL = import.meta.env.VITE_REST_API_URL ?? '';

interface InterviewSessionProps {
  sessionId: string;
  mode: 'voice' | 'text';
  preloadedTurns?: Turn[];
  readOnly?: boolean;
  /** Optional photoId for photo-prompted sessions (02-05). Forwarded to useSession. */
  photoId?: string;
  /**
   * Optional life area ID chosen from the pre-session suggestion card (Phase 7).
   * When set, included in the [BEGIN_SESSION] sentinel to provide a soft focus hint.
   */
  focusAreaId?: string;
  /**
   * Optional story topic title chosen from the pre-session suggestion (Phase 30).
   * When set, included in the [BEGIN_SESSION] sentinel as a strong focus instruction.
   */
  topicTitle?: string;
  /** Area tags from the post-session classifier — undefined = still analyzing */
  areaTags?: string[];
  /** ISO timestamp of last classification */
  areaTagsAt?: string;
}

export function InterviewSession({ sessionId, mode, preloadedTurns, readOnly = false, photoId, focusAreaId, topicTitle, areaTags: _areaTags, areaTagsAt: _areaTagsAt }: InterviewSessionProps) {
  const navigate = useNavigate();

  // Photo thumbnail state — fetch viewUrl for photo-prompted sessions
  const [photoViewUrl, setPhotoViewUrl] = useState<string | null>(null);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);

  useEffect(() => {
    if (!photoId || readOnly) return;
    let cancelled = false;
    fetchWithAuth(`${REST_API_URL}/photos`)
      .then(r => r.json() as Promise<{ photos: Array<{ photoId: string; viewUrl: string | null }> }>)
      .then(data => {
        if (cancelled) return;
        const match = data.photos.find(p => p.photoId === photoId);
        if (match?.viewUrl) setPhotoViewUrl(match.viewUrl);
      })
      .catch(() => { /* non-fatal — photo thumbnail just won't show */ });
    return () => { cancelled = true; };
  }, [photoId, readOnly]);

  // Dismiss lightbox on Escape
  useEffect(() => {
    if (!showPhotoLightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowPhotoLightbox(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPhotoLightbox]);

  const {
    mode: sessionMode,
    turns,
    streamingAiText,
    partialTranscript,
    error,
    isListening,
    startMic,
    stopMic,
    sendTextTurn,
    endSession,
    hasPendingTurn,
    retryTurn,
    isAiAudioPlaying,
    interruptAi,
    setVoiceMedia,
    voiceMediaSentCount,
  } = useSession({
    sessionId,
    mode,
    preloadedTurns,
    photoId,
    focusAreaId,
    topicTitle,
    onEnded: () => navigate('/'),
  });

  // AudioContext is primed in Session.tsx handleModeSelect during the user gesture.
  // MicButton.onStart calls resumeAudioContext() as a belt-and-suspenders fallback.

  // ── Voice-mode photo queue ──────────────────────────────────────────────────
  // Uploaded photos queue as chips and ride along with the next voice turn's
  // user_turn frame (mediaPhotoIds). useSession clears the queue on send and
  // bumps voiceMediaSentCount so the sent chips can be removed here.
  const [voicePhotos, setVoicePhotos] = useState<PendingPhoto[]>([]);
  const voicePhotoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const ready = voicePhotos.filter(p => p.photoId !== null);
    setVoiceMedia(ready.map(p => p.photoId as string), ready.map(p => p.previewUrl));
  }, [voicePhotos, setVoiceMedia]);

  useEffect(() => {
    if (voiceMediaSentCount === 0) return;
    // Sent photos are the ready ones — keep chips still uploading or errored
    setVoicePhotos(prev => prev.filter(p => p.photoId === null));
  }, [voiceMediaSentCount]);

  const handleVoicePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const room = MAX_CHAT_PHOTOS - voicePhotos.length;
    for (const file of files.slice(0, Math.max(room, 0))) {
      const localId = newLocalPhotoId();
      if (!CHAT_IMAGE_TYPES.includes(file.type)) {
        setVoicePhotos(prev => [...prev, { localId, photoId: null, previewUrl: '', uploading: false, error: 'Only JPEG, PNG, and WebP are supported' }]);
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      setVoicePhotos(prev => [...prev, { localId, photoId: null, previewUrl, uploading: true }]);
      void uploadChatPhoto(file, (patch) => {
        setVoicePhotos(prev => prev.map(p => p.localId === localId ? { ...p, ...patch } : p));
      });
    }
  };

  const removeVoicePhoto = (localId: string) => {
    setVoicePhotos(prev => {
      const target = prev.find(p => p.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p.localId !== localId);
    });
  };

  const handleEndSession = () => {
    endSession();
  };

  const handleResume = () => {
    // The store's resume() sets mode='connecting' which re-enables the WebSocket
    // This is handled inside useSession via the pause/resume flow
    // Re-mounting by navigating to the same URL triggers reconnect
    window.location.reload();
  };

  // ── Derived turn-loop state (mobile interaction model) ─────────────────────
  // AI is "responding" while text streams (ai_speaking) or while voice audio is
  // still draining after ai_done (isAiAudioPlaying).
  const isAiResponding = sessionMode === 'ai_speaking' || (mode === 'voice' && isAiAudioPlaying);
  const isProcessing = sessionMode === 'processing';
  const isConnecting = sessionMode === 'connecting';

  // ── Space bar shortcut for voice mode ──────────────────────────────────────
  useEffect(() => {
    if (mode !== 'voice' || readOnly) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      if (isListening) {
        void stopMic();
      } else if (isAiResponding) {
        interruptAi();
      } else if (sessionMode === 'gary_speaking') {
        void startMic();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, readOnly, isListening, isAiResponding, sessionMode, startMic, stopMic, interruptAi]);

  const isMicDisabled = readOnly || isConnecting || sessionMode === 'paused' || sessionMode === 'error';
  const isTextDisabled = sessionMode !== 'gary_speaking' || readOnly;
  const isPaused = sessionMode === 'paused';

  // Large status line driving the voice turn loop
  const voiceStatus = isListening
    ? { text: 'Listening…', className: 'text-red-600' }
    : isProcessing
    ? { text: 'Transcribing your recording…', className: 'text-stone-500' }
    : isAiResponding
    ? { text: 'AI is responding…', className: 'text-amber-600' }
    : isConnecting
    ? { text: 'Connecting…', className: 'text-stone-400' }
    : { text: 'Your turn to speak', className: 'text-stone-700' };

  const voiceHint = isListening
    ? 'Tap or press space when you finish your answer'
    : isProcessing
    ? 'One moment…'
    : isAiResponding
    ? 'Tap to stop and take your turn'
    : isConnecting
    ? ''
    : 'Tap the mic or press space to speak';

  // Same status model for text mode — large line above the input bar
  const textStatus = isProcessing || sessionMode === 'ai_speaking'
    ? { text: 'AI is responding…', className: 'text-amber-600' }
    : isConnecting
    ? { text: 'Connecting…', className: 'text-stone-400' }
    : { text: 'Your turn — type your answer', className: 'text-stone-700' };

  // Format session date
  const sessionDate = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white border-b border-stone-200 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-stone-500 hover:text-stone-800 transition-colors"
            aria-label="Back to dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
              <path fillRule="evenodd" d="M11.03 3.97a.75.75 0 010 1.06l-6.22 6.22H21a.75.75 0 010 1.5H4.81l6.22 6.22a.75.75 0 11-1.06 1.06l-7.5-7.5a.75.75 0 010-1.06l7.5-7.5a.75.75 0 011.06 0z" clipRule="evenodd" />
            </svg>
          </button>
          <div>
            <p className="text-xs text-stone-500">{sessionDate}</p>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  mode === 'voice'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-sky-100 text-sky-700'
                }`}
              >
                {mode === 'voice' ? '🎙️ Voice' : '⌨️ Text'}
              </span>
              {readOnly && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600">
                  Read only
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {photoViewUrl && (
            <button
              type="button"
              onClick={() => setShowPhotoLightbox(true)}
              className="rounded-lg overflow-hidden border-2 border-stone-200 hover:border-amber-400 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="View session photo"
              title="View photo"
            >
              <img src={photoViewUrl} alt="Session photo" className="w-10 h-10 object-cover" />
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={handleEndSession}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              End Session
            </button>
          )}
        </div>
      </header>

      {/* Pause banner */}
      {isPaused && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-amber-800 font-medium">
            Welcome back — your session is paused.
          </p>
          <button
            type="button"
            onClick={handleResume}
            className="text-sm font-semibold text-amber-700 hover:text-amber-900 underline transition-colors"
          >
            Resume
          </button>
        </div>
      )}

      {/* Error banner — when a user turn is pending, Reconnect re-sends it instead of reloading */}
      {error && sessionMode === 'error' && (
        <ErrorBanner
          error={error}
          onReconnect={hasPendingTurn ? retryTurn : handleResume}
          reconnectLabel={hasPendingTurn ? 'Retry' : 'Reconnect'}
        />
      )}

      {/* Transcript */}
      <TranscriptStream
        turns={turns}
        streamingAiText={streamingAiText}
        partialTranscript={partialTranscript}
      />

      {/* Footer: status-driven turn loop (hidden if read-only) */}
      {!readOnly && (
        <footer className="bg-white border-t border-stone-200">
          {mode === 'voice' ? (
            <div className="flex flex-col items-center pt-5 pb-4 gap-3">
              <input
                ref={voicePhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="sr-only"
                aria-label="Attach photos"
                onChange={handleVoicePhotoChange}
                disabled={readOnly}
              />

              {/* Queued photo chips — ride along with the next voice turn */}
              {voicePhotos.length > 0 && (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="flex items-center justify-center gap-2 flex-wrap px-4">
                    {voicePhotos.map(photo => (
                      photo.error ? (
                        <div key={photo.localId} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-red-50 border border-red-200">
                          <span className="text-xs text-red-600">Upload failed</span>
                          <button type="button" onClick={() => removeVoicePhoto(photo.localId)} aria-label="Dismiss failed photo"
                            className="text-red-400 hover:text-red-700">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
                              <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div key={photo.localId} className="relative">
                          <img
                            src={photo.previewUrl}
                            alt="Queued photo"
                            className="h-12 w-12 rounded-lg object-cover border border-stone-200"
                          />
                          {photo.uploading ? (
                            <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                            </div>
                          ) : (
                            <button type="button" onClick={() => removeVoicePhoto(photo.localId)} aria-label="Remove photo"
                              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-stone-700 text-white flex items-center justify-center hover:bg-stone-900">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-2.5 h-2.5" aria-hidden="true">
                                <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                  <p className="text-xs text-stone-400">Will be included with your next reply</p>
                </div>
              )}

              <p
                className={`text-xl font-semibold ${voiceStatus.className}`}
                aria-live="polite"
              >
                {voiceStatus.text}
              </p>
              <div className="relative flex items-center justify-center w-full">
                <MicButton
                  onStart={startMic}
                  onStop={stopMic}
                  onInterrupt={interruptAi}
                  isListening={isListening}
                  processing={isProcessing}
                  aiSpeaking={isAiResponding}
                  disabled={isMicDisabled}
                  onSwitchToText={() => navigate(`/session/${sessionId}?mode=text`)}
                />
                <button
                  type="button"
                  onClick={() => voicePhotoInputRef.current?.click()}
                  disabled={readOnly || voicePhotos.length >= MAX_CHAT_PHOTOS}
                  aria-label="Attach photos to your next reply"
                  title="Attach photos to your next reply"
                  className={[
                    'absolute top-1/2 -translate-y-1/2 left-1/2 ml-16',
                    'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-amber-400',
                    readOnly || voicePhotos.length >= MAX_CHAT_PHOTOS
                      ? 'text-stone-300 cursor-not-allowed'
                      : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100',
                  ].join(' ')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                    <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-stone-400 text-center min-h-4">{voiceHint}</p>
              <button
                type="button"
                onClick={handleEndSession}
                className="text-xs text-stone-400 hover:text-red-600 transition-colors underline"
              >
                End Session
              </button>
            </div>
          ) : (
            <>
              <p
                className={`pt-3 text-center text-base font-semibold ${textStatus.className}`}
                aria-live="polite"
              >
                {textStatus.text}
              </p>
              <TextInputBar
                onSend={sendTextTurn}
                disabled={isTextDisabled}
              />
            </>
          )}
        </footer>
      )}

      {/* Photo lightbox overlay */}
      {showPhotoLightbox && photoViewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowPhotoLightbox(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Session photo"
        >
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowPhotoLightbox(false)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm font-medium"
              aria-label="Close photo"
            >
              Close ✕
            </button>
            <img
              src={photoViewUrl}
              alt="Session photo"
              className="w-full rounded-xl shadow-2xl object-contain max-h-[80vh]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── ErrorBanner ───────────────────────────────────────────────────────────────

function ErrorBanner({ error, onReconnect, reconnectLabel = 'Reconnect' }: { error: { code: string; message: string }; onReconnect: () => void; reconnectLabel?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `Error: ${error.message}\nCode: ${error.code}\nTime: ${new Date().toISOString()}`;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between gap-3">
      <p className="text-sm text-red-700 min-w-0 truncate">{error.message}</p>
      <div className="flex items-center gap-3 flex-none">
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-red-500 hover:text-red-700 underline transition-colors"
        >
          {copied ? 'Copied!' : 'Copy error'}
        </button>
        <button
          type="button"
          onClick={onReconnect}
          className="text-sm font-semibold text-red-600 hover:text-red-800 underline transition-colors"
        >
          {reconnectLabel}
        </button>
      </div>
    </div>
  );
}
